/**
 * **The Contacts drawer's real field list, as attribute specs** (LF-6305 / [ATTR-4]).
 *
 * The prototype's `components/prototypes/form-system/contact-attribute-specs.tsx`
 * (767 lines) is the worked example to port, not to copy — it is written against a
 * flat local object (`ContactAttributeValues`) that submits nothing. Every
 * `toPatch` here is written against the real `Partial<Contact>` API and verified
 * against `lib/services/contacts/types.ts`, `ContactFormCard.tsx` and
 * `RoleSection.tsx` — the production form this treatment replaces.
 *
 * ## ⚠️ Which half of that sentence is the reference (LF-6350 / [ATTR-8])
 *
 * The line above is right about the **patches** and was wrong about the **shape**,
 * and the distinction cost a ticket. `RoleSection.tsx` / `ContactFormCard.tsx` are
 * the source of truth for *what a commit sends* — the real keys, the reset side
 * effects, the live validators. They are **not** the source of truth for the field
 * list, its order, its labels or its grouping: they are the legacy form this
 * treatment exists to replace, so sourcing the shape from them re-imported the
 * layout decisions the treatment had already overturned (`Date of birth` and
 * `Minor` under `Role`, gated by switches sitting below them).
 *
 * For shape, the reference is the prototype's `regroupedSections` — **story 7a**,
 * the direction the epic chose. Nothing between [ATTR-4] and [ATTR-6] compared the
 * rendered drawer against the rendered story, which is why a drift this visible
 * survived three tickets. LF-6350 reconciled them by measuring both renders at
 * 672px; `__tests__/contact-attribute-specs.test.ts` now pins the resulting order
 * and grouping so the next change has to be deliberate.
 *
 * ## Correction to the plan's stated builder signature
 *
 * `frontend/.claude/tasks/20260811-lf-6301-attribute-treatment-contacts-drawer/plan.md`
 * names the builder `(contact: Contact) => AttributeSpec[]`. Re-deriving the full
 * conditional set from `RoleSection` and `ContactFormCard` (as the ticket
 * instructs) shows several gates — the LPR/Beneficiary/relationship/deceased
 * controls, the matter-notes row, the bank-verification file note — depend on
 * `isEstate` and matter-context flags that are **props** passed into those
 * components, not data on `Contact` itself (see `ContactFormSectionProps` /
 * `ContactDetailsDrawer`). A bare `(contact) => specs` builder cannot reproduce
 * them, so {@link buildContactAttributeSpecs} takes a second
 * {@link ContactAttributeSpecOptions} argument — the same three flags
 * `ContactFormCard` already threads (`isEstate`, `showMatterNotes` and the
 * `contacts-page` feature flag that gates general notes).
 *
 * ## Two more nuances the flat prototype could not surface
 *
 * - **`isMinor` is UI-only and must never be sent.** `Contact.isMinor` is
 *   explicitly commented "DO NOT send to backend" — the persisted field is
 *   `isOfAge`, its inverse. `toPatch` maps `isOfAge = !isMinor`, mirroring
 *   `ContactFormCard`'s own submit-time conversion.
 * - **The LPR role's `useEffect` reset becomes part of the Role row's own
 *   `toPatch`.** `RoleSection` resets `isProvingLpr`/`nonProvingReason` when the
 *   role stops being `'executor'` via a form-level effect; a stateless attribute
 *   row has no effect to run, so the Role row's `toPatch` folds the same reset
 *   into the one patch it sends.
 */

import {
  bankAccountCopyText,
  bankAccountVerifiedBadge,
  bsbDigits,
  formatBankAccountSummary
} from '@/components/attributes';
import type {
  AttributeBankAccount,
  AttributeNameParts,
  AttributeSpec
} from '@/components/attributes';
import {
  isDeathNonProvingReason,
  LEGAL_PERSONAL_REPRESENTATIVE_ROLES,
  NON_PROVING_REASON_LABELS,
  NON_PROVING_REASONS,
  RELATIONSHIP_LABELS,
  RELATIONSHIP_OPTIONS,
  type NonProvingReason
} from '@/lib/constants';
import { formatAddress } from '@/lib/formatters/contact-formatters';
import { formatSpelledDate } from '@/components/leads/lead-row-format';
import type { Contact } from '@/lib/services/contacts/types';
import type { PostalAddress } from '@/lib/services/matters/types';
import { isOrganisationContact } from '@/lib/services/contacts/utils';
import { validateEmail } from '@/lib/validators/contact-validators';

/** A section of the grid: a small uppercase title and the rows under it (§1.10 — "Groups: rule"). */
export interface ContactAttributeGroup {
  /**
   * `null` continues the section above it rather than opening one of its own
   * (`AttributeSections` §7b) — the Notes rows use it (VALE-863).
   */
  title: string | null;
  fields: AttributeSpec<Partial<Contact>>[];
}

/**
 * The Role group's two titles (VALE-326) — exported so
 * `contact-attribute-grid.tsx`'s `MATTER_SCOPED_GROUPS` gate binds to these
 * exact values instead of restating them as its own literals, which is how a
 * non-estate matter ended up under the estate's heading in the first place.
 */
export const ROLE_GROUP_TITLE_ESTATE = 'Role in the estate';
export const ROLE_GROUP_TITLE_NON_ESTATE = 'Role on this matter';

/**
 * **The two name rows' labels, exported for the same reason the Role titles
 * are** (VALE-863). `contact-attribute-grid.tsx` asks `AttributeGrid` to open
 * one of them by *label* after a contact-type switch — the auto-open signal is
 * matched on the string — so a restated literal there goes stale the moment a
 * label changes and the auto-open silently stops finding its row. That is
 * exactly what consolidating `First name` into `Name` would have done.
 */
export const PERSONAL_DETAILS_GROUP_TITLE = 'Personal details';

export const INDIVIDUAL_NAME_LABEL = 'Name';
export const ADDRESS_LABEL = 'Address';
export const ORGANISATION_NAME_LABEL = 'Organisation name';
/**
 * The words the client intake form already puts to the client, kept identical
 * here so a client-supplied value appears under the label they saw. Not
 * "Salutation": that ordinarily means the whole greeting line, which invites
 * "Dear Bob" and renders "Dear Dear Bob," (VALE-989).
 */
export const PREFERRED_NAME_LABEL = 'Preferred name';

/**
 * The matter-context flags `RoleSection` / `ContactFormCard` take as props
 * rather than reading off `Contact` — see the module doc's "Correction" section.
 */
export interface ContactAttributeSpecOptions {
  /**
   * Estate matter vs non-estate (LF-5788). Estate matters show the LPR /
   * Beneficiary / relationship / deceased controls; non-estate matters show a
   * single client-only `Role — Client` row. A global (non-matter) contact
   * passes `false`, matching `ContactDetailsDrawer`'s
   * `isEstateAdminType(matter?.type)`.
   */
  isEstate: boolean;
  /**
   * Whether this contact belongs to a specific matter (`hasMatterId` in
   * `ContactDetailsDrawer`). Gates the per-matter Notes row and, combined with
   * `isEstate` and `isBeneficiary`, the bank-verification file note — exactly
   * `showMatterNotes` / `showFileNote`'s existing derivation.
   */
  hasMatterContext: boolean;
  /** `hasFeature('contacts-page')` — gates the general-notes row. */
  showGeneralNotes: boolean;
  /**
   * **The type the reader has chosen, when it is not yet the type the record
   * holds** (LF-6359 / [ATTR-9] §4).
   *
   * Production stores no contact-type column — `isOrganisationContact` derives
   * it from whether `organisationName` is set — so the moment between *"I want
   * this to be an organisation"* and *"the organisation has a name"* has nowhere
   * to live on the record. It lives here, in the host, for exactly that gap.
   *
   * `undefined` (the usual case) means *"read the type off the contact"*.
   */
  contactType?: 'individual' | 'organisation';
  /**
   * The `Contact type` row's commit, which sets {@link contactType} rather than
   * sending a patch — see `AttributeSpec.localCommit`.
   *
   * Absent on the flag-off / test paths, where the row simply falls back to
   * being read-only in practice: with no host to record the choice, the derived
   * type is the only answer there is.
   *
   * **Called only for the switch this record cannot complete on its own**
   * (LF-6374). The other direction — see {@link contactTypeField}'s own doc —
   * has something to send immediately and never reaches this callback at all.
   */
  onContactTypeChange?: (next: 'individual' | 'organisation') => void;
  /**
   * **A person on an enquiry** (VALE-2061), before any matter exists.
   *
   * Their own details are the standard ones. Their role group is the three
   * facts an enquiry holds about a person, whether they are an LPR, their role
   * and their relationship, and nothing else: beneficiary, proving, bank and
   * matter notes are matter facts the enquiry has nowhere to keep. The role may
   * be cleared back to not yet known.
   */
  enquiryContact?: boolean;
}

const asText = (draft: unknown): string =>
  typeof draft === 'string' ? draft : '';

/** `''` → `null`: an emptied field means *clear it* on a PATCH. */
const blankToNull = (value: string): string | null =>
  value.length === 0 ? null : value;

// =============================================================================
// Factories
// =============================================================================

/** Free text that may legitimately be absent — blanking it clears the column. */
function textField<K extends keyof Contact>(
  label: string,
  key: K,
  value: string | null | undefined,
  extra: Partial<AttributeSpec<Partial<Contact>>> = {}
): AttributeSpec<Partial<Contact>> {
  return {
    label,
    display: value || null,
    draft: value ?? '',
    editor: { kind: 'text' },
    toPatch: (draft) =>
      ({ [key]: blankToNull(asText(draft)) }) as Partial<Contact>,
    ...extra
  };
}

/**
 * Free text the contact cannot be without — mirrors `NameSection`'s `required`
 * marker on First / Last / Organisation name. A drawer with no Save button has
 * no validate-before-send moment, so refusing here (with a reason, keeping the
 * field open) is the only thing standing between a blanked required name and a
 * silent data-loss bug.
 */
function requiredTextField<K extends keyof Contact>(
  label: string,
  key: K,
  value: string | null | undefined
): AttributeSpec<Partial<Contact>> {
  return {
    label,
    display: value || null,
    draft: value ?? '',
    editor: { kind: 'text' },
    required: true,
    validate: (draft) =>
      asText(draft).trim().length === 0 ? `${label} is required.` : null,
    toPatch: (draft) => ({ [key]: asText(draft) }) as Partial<Contact>
  };
}

/*
  `selectField` used to live here — a closed vocabulary rendered as a subtle badge at rest. It is
  gone because relationship-to-deceased was its only caller, and that field moved to
  `selectWithCustom` once production showed the vocabulary was never closed. The other select-backed
  rows in this file (contact type, the LPR role, the non-proving reason) are genuinely closed sets
  and declare their editors inline, each with a `display` rule of its own.

  Worth resurrecting only for a field that is genuinely closed AND needs no per-field display
  handling; reaching for it to avoid writing four lines is what made the relationship row's own
  display silently wrong for the ~6% of rows holding an unlisted value (it resolved the label from
  the options and returned null on a miss, so those read as "not recorded").
*/

/** A native date picker over a `YYYY-MM-DD` draft, read out spelled per §1's date convention. */
function dateField<K extends keyof Contact>(
  label: string,
  key: K,
  value: string | null | undefined,
  extra: Partial<AttributeSpec<Partial<Contact>>> = {}
): AttributeSpec<Partial<Contact>> {
  return {
    label,
    display: value ? formatSpelledDate(`${value}T00:00:00`) : null,
    draft: value ?? '',
    editor: { kind: 'date' },
    toPatch: (draft) =>
      ({ [key]: blankToNull(asText(draft)) }) as Partial<Contact>,
    ...extra
  };
}

/**
 * A boolean read out as the `Yes` / `No` pair (§1.8), for a flag whose PATCH
 * key and reset side-effects vary per field — so unlike `text`/`select`/`date`
 * this is not a one-line factory, callers write the row's `toPatch` directly.
 * Kept here only to share the draft/display derivation.
 */
function flagDraft(value: boolean | null | undefined): {
  display: string;
  draft: string;
} {
  const flag = Boolean(value);
  return { display: flag ? 'Yes' : 'No', draft: flag ? 'yes' : 'no' };
}

const asFlag = (draft: unknown): boolean => asText(draft) === 'yes';

/**
 * **A flag whose third state is real** — `null` meaning *nobody has determined
 * this yet*, kept distinct from `No` (VALE-863, on VALE-795's `triState`).
 *
 * {@link flagDraft} coerces with `Boolean(value)`, which is right for a flag
 * that genuinely starts `false`. It is wrong for one the column stores as
 * `boolean | null`: an undetermined value renders as a confident `No`, and the
 * row then asserts a decision nobody made.
 */
function triStateDraft(value: boolean | null | undefined): {
  display: string | null;
  draft: string;
} {
  if (value === true) return { display: 'Yes', draft: 'yes' };
  if (value === false) return { display: 'No', draft: 'no' };
  return { display: null, draft: 'unknown' };
}

/**
 * The one boundary between the grid's `PostalAddress` draft and `Contact`'s own
 * address shape — a *third* shape (`attribute-address-draft.ts`'s doc), adding
 * `businessName` / `poBox` and making every member `| null`. Neither
 * `toStructuredAddress` nor `toAddressDraft` applies here; this maps explicitly
 * and, on commit, **preserves the two sibling keys it does not own** — the
 * `AddressSection.tsx` precedent (spread the existing address, overwrite only
 * the five shared parts) — so a manual address edit never silently drops the
 * business name or PO box.
 */

function addressAttribute(
  contact: Partial<Contact>
): AttributeSpec<Partial<Contact>> {
  const address = contact.address;
  const formatted = address
    ? formatAddress({
        businessName: address.businessName ?? undefined,
        streetAddress: address.streetAddress ?? undefined,
        suburb: address.suburb ?? undefined,
        state: address.state ?? undefined,
        postcode: address.postcode ?? undefined,
        poBox: address.poBox ?? undefined,
        country: address.country ?? undefined
      })
    : '';
  const draft: PostalAddress | null = address
    ? {
        streetAddress: address.streetAddress ?? undefined,
        suburb: address.suburb ?? undefined,
        state: address.state ?? undefined,
        postcode: address.postcode ?? undefined,
        country: address.country ?? undefined
      }
    : null;

  return {
    label: ADDRESS_LABEL,
    display: formatted || null,
    draft,
    editor: { kind: 'address' },
    copyable: true,
    copyText: formatted,
    /*
      **No longer `fullWidth`** (VALE-863): it pairs with `Other addresses` in
      the right-hand column, so it takes half the row like `Name` does.
    */
    /*
      **Ellipsises rather than wrapping**, which is this row opting out of a
      rule rather than the rule changing — see {@link AttributeSpec.wraps}. An
      `address` derives `wraps` because a truncated one was a shipped defect
      (LF-6388), and that derivation assumes the row has a readable width. At
      half a column it does not: nearly every real address took two lines, and
      the owner's call is that an ellipsis reads better there. The full value
      stays reachable — `title` carries it, and the popover shows every part.
    */
    wraps: false,
    clearTo: null,
    toPatch: (nextDraft) => {
      if (typeof nextDraft === 'string') return null;
      const next = nextDraft as PostalAddress | null;
      return {
        address: {
          ...(address ?? {}),
          streetAddress: next?.streetAddress ?? null,
          suburb: next?.suburb ?? null,
          state: next?.state ?? null,
          postcode: next?.postcode ?? null,
          country: next?.country ?? null
        }
      };
    }
  };
}

// =============================================================================
// The field list
// =============================================================================

/**
 * The real contact field list, grouped in the 7a "Groups: rule" format.
 *
 * A pure function of `contact` and `options` — no hooks, no side effects — so
 * every conditional row is a unit-testable `hidden` predicate rather than a
 * mount-time branch. [ATTR-5] calls this on every render with the latest
 * contact snapshot (the interaction contract's server-rejection re-open, and
 * every successful commit, both refetch and rebuild the spec list).
 */
export function buildContactAttributeSpecs(
  contact: Partial<Contact>,
  options: ContactAttributeSpecOptions
): ContactAttributeGroup[] {
  const {
    isEstate,
    hasMatterContext,
    showGeneralNotes,
    contactType,
    onContactTypeChange,
    enquiryContact = false
  } = options;

  /*
    The host's pending choice wins over the record's derived type — see
    `ContactAttributeSpecOptions.contactType`. With no pending choice this is
    exactly `isOrganisationContact(contact)`, which is every other caller.
  */
  const isOrg = contactType
    ? contactType === 'organisation'
    : isOrganisationContact(contact);

  /*
    **Completing a switch back to individual has one extra thing to say.** The
    type is derived from `organisationName`, so a contact who is being turned
    back into an individual is still an organisation to every other reader until
    that name is nulled — and nulling it is not a statement the `Contact type`
    row can make on its own, because a contact with neither an organisation name
    nor a personal name is precisely what `UpdateContactRequestSchema`'s
    `submitsBothNames` refine exists to refuse.

    So the row that *does* have a name to give carries it. See
    `individualNamePatch`.
  */
  const clearingOrganisation = !isOrg && isOrganisationContact(contact);

  /*
    **The other direction of a switch has nothing to give until the reader
    types it — except when the record already holds it** (LF-6374, the owner's
    settled call on the ticket). This is decidable from the *stored* record
    alone, rather than waiting to see which value gets chosen:

    · **Organisation → Individual, and the record already has a first and last
      name** (typically leftover from before it was ever made an organisation —
      see the "letting old values linger" note below): the switch persists on
      itself, immediately, via `toPatch` below. One action, saved.
    · **Every Individual → Organisation, and Organisation → Individual with no
      personal name to fall back on:** the switch cannot complete on its own —
      the missing name is the very thing the type is derived from — so it stays
      `localCommit`'s pending branch below, and `contact-attribute-grid.tsx`
      leads the reader into the field that completes it.

    **Bound to `isOrganisationContact(contact)`, never to `isOrg`.** `isOrg`
    folds in the *pending* choice, and a still-pending switch is exactly the
    state where the two disagree: pick Organisation on a no-name individual
    (a legitimate pending choice), then reconsider and pick Individual again
    before it completes — `isOrg` reads `true` (from the pending choice) while
    the stored record is still genuinely individual. Deciding immediacy from
    `isOrg` there would route the reconsidered pick through `toPatch` instead
    of `localCommit`, send a no-op `{ organisationName: null }` (already
    null), and never call `onContactTypeChange` to clear the pending choice —
    leaving the grid stuck on the organisation branch until the drawer closes.
    The stored record, not the pending choice, is what "does this direction
    have something to send immediately" has to be asked of.
  */
  const hasPersonalName = Boolean(
    contact.firstName?.trim() && contact.lastName?.trim()
  );
  const completesSwitchImmediately =
    isOrganisationContact(contact) && hasPersonalName;

  const isLpr = Boolean(contact.isLegalPersonalRepresentative);
  const lprRole = contact.legalPersonalRepresentativeRole ?? null;
  const isExecutor = lprRole === 'executor';
  // Three states, not two: `null` means the firm has not yet established
  // whether this executor is proving. Coalescing it to `true` here would
  // reach `triStateDraft` as an answer nobody gave — see `provingField`.
  const isProvingLpr = contact.isProvingLpr ?? null;
  const isBeneficiary = Boolean(contact.isBeneficiary);
  const isMinor =
    contact.isMinor ??
    (typeof contact.isOfAge === 'boolean' ? !contact.isOfAge : false);
  const isDeceased = contact.isDeceased ?? Boolean(contact.dateOfDeath);
  const isBankVerified = Boolean(contact.isBankVerified);

  // ---------------------------------------------------------------------------
  // Personal details
  // ---------------------------------------------------------------------------

  const contactTypeField: AttributeSpec<Partial<Contact>> = {
    label: 'Contact type',
    display: isOrg ? 'Organisation' : 'Individual',
    draft: isOrg ? 'organisation' : 'individual',
    editor: {
      kind: 'select',
      options: [
        { value: 'individual', label: 'Individual' },
        { value: 'organisation', label: 'Organisation' }
      ]
    },
    /*
      Every contact is one or the other, so there is nothing to clear *to* — and
      this row does not even persist a value of its own (`localCommit` below
      re-draws the row list instead). A `×` would offer to empty a field that has
      no empty state.
    */
    required: true,
    /*
      **The switch changes which rows exist; it does not empty the ones it is
      leaving** (LF-6359 / [ATTR-9] §4, story 7a).

      It used to send a compound patch that nulled the outgoing type's fields —
      `firstName: ''`, `lastName: ''`, `organisationName: ''` — reasoning that
      the backend's `Object.assign` only touches keys present in the payload, so
      an omitted name would linger server-side. It never reached the backend to
      find out: `UpdateContactRequestSchema`'s `submitsBothNames` refine saw both
      name keys present and both empty, with no organisation name to satisfy it,
      and rejected the patch **client-side** in `client.ts` before any HTTP call.
      Both directions failed, identically, for the same reason. That refine
      reasons carefully about single-field PATCHes (LF-6306) and never considered
      the compound type-switch — the one patch on this surface that empties both
      names on purpose.

      Story 7a does not have the problem because it does not have the patch: its
      `Contact type` row writes only `contactType` and the field list is a **real
      branch**, so the leaving type's values simply stop being rendered. This is
      that, on a record whose type is derived rather than stored — hence
      `localCommit` (the host holds the choice) and, for the pending direction,
      no `toPatch` at all.

      Letting the old values linger is now a feature rather than the defect the
      original comment feared: `organisationName` is the discriminator everywhere
      (`isOrganisationContact`, `hasRequiredContactName`), so a lingering
      `firstName` on an organisation is invisible — and it repopulates the rows
      if the contact is ever switched back, which is the reader's own data
      returning rather than a surprise. It is also exactly what makes
      `completesSwitchImmediately` legal below: those lingering names are the
      "record already holds it" case.

      **`localCommit` is only wired for the direction that has to wait**
      (LF-6374). The record-already-qualifies direction below sends a real
      patch instead — see that branch for why nulling `organisationName` alone
      is enough and does not need `submitsBothNames`'s cooperation.
    */
    localCommit: completesSwitchImmediately
      ? undefined
      : (draft) => {
          const next = asText(draft);
          onContactTypeChange?.(
            next === 'organisation' ? 'organisation' : 'individual'
          );
        },
    /*
      **The one direction that can complete on the switch itself** (LF-6374):
      Organisation → Individual, where the record already carries the first and
      last name the individual branch needs. Sending `{ organisationName: null
      }` alone — no `firstName` / `lastName` keys at all — means
      `UpdateContactRequestSchema`'s `submitsBothNames` refine never engages (it
      only judges a payload that submits *both* name keys), so this needs no
      cooperation from the name rows the way the pending direction's completing
      commit does (see `individualNamePatch` below).

      Unreachable whenever `localCommit` is set — the commit path prefers it and
      never calls this. Kept, and kept honest, rather than returning `null`
      unconditionally: `toPatch` is required by `AttributeSpec`, and a row that
      lies about what it would send is worse than one that never sends it.
    */
    toPatch: (draft) =>
      completesSwitchImmediately && asText(draft) === 'individual'
        ? { organisationName: null }
        : null
  };

  /**
   * What an individual's name rows commit — plus the organisation name's
   * removal, on the one commit that is completing a switch back.
   *
   * **This is what makes the switch legal.** `{ lastName: 'Smith',
   * organisationName: null }` submits *one* name key, so `submitsBothNames` is
   * false and the refine passes untouched — where the old
   * `{ firstName: '', lastName: '', organisationName: '' }` submitted both and
   * emptied both. The contact is never nameless at any point, which is the
   * invariant that refine exists to protect and the reason widening it was the
   * wrong fix.
   */
  const individualNamePatch = (patch: Partial<Contact>): Partial<Contact> =>
    clearingOrganisation ? { ...patch, organisationName: null } : patch;

  /*
    **A real branch, not a `hidden` gate** — story 7a's own distinction, quoted
    in the prototype: *"Only rows whose gate is a value on the same record work
    [as `hidden`]; the individual/organisation split genuinely changes which
    fields exist, so it stays a real branch."*

    `hidden` earns its place where a row collapses and the ones below it glide
    into the gap ([ATTR-3]'s motion) — `Date of death` under `Deceased`. A row
    being *replaced* by another is not that: there is no before/after of the
    same row to animate, and gating it would leave the grid stepping `Tab`
    through a name that does not apply to this contact at all.
  */

  /**
   * **One `Name` row, not three** (VALE-863).
   *
   * `First name` / `Middle name` / `Last name` were three sibling rows, which
   * cost the top of the form three of its lines to say one thing and put the
   * first thing a reader meets — the person's name — behind `Contact type`.
   * Tom's 27 Aug review asked for the consolidated field directly, with the
   * Attio record header as the reference.
   *
   * **The `name` editor already existed** (VALE-129) and is the same popover
   * the matter's `Deceased` row opens, so this is a spec change rather than a
   * new control: four rows folded into one on that surface for the same reason.
   * The parts stay separate in the draft and the patch — a name flattened to
   * one line has to be parsed back, and compound surnames are exactly what that
   * loses.
   *
   * **No `titleOptions`, because `Contact` has no title column** — only
   * `firstName` / `middleName` / `lastName`. The editor's Title part is
   * optional for this consumer (VALE-863); offering it here would render a
   * control with nowhere to commit.
   */
  const individualNameParts: AttributeNameParts = {
    firstName: contact.firstName ?? '',
    middleName: contact.middleName ?? '',
    lastName: contact.lastName ?? ''
  };
  const individualNameRecorded = Object.values(individualNameParts).some(
    (part) => (part ?? '').trim().length > 0
  );
  /*
    Joined here rather than through a shared helper: `formatDecedentName` reads
    `Partial<Matter>` and falls back to the literal `Unnamed`, both wrong for a
    contact, and `contactName`'s own formatter covers the organisation case this
    row is the individual branch of. Absent parts are skipped so a contact with
    no middle name does not render a double space.
  */
  const individualNameFormatted = [
    individualNameParts.firstName,
    individualNameParts.middleName,
    individualNameParts.lastName
  ]
    .filter((part) => (part ?? '').trim().length > 0)
    .join(' ');

  const individualNameField: AttributeSpec<Partial<Contact>> = {
    label: INDIVIDUAL_NAME_LABEL,
    display: individualNameFormatted || null,
    draft: individualNameRecorded ? individualNameParts : null,
    editor: { kind: 'name' },
    required: true,
    /*
      A `name` derives no copy affordance (`offersCopy` covers `text`), and
      folding the three rows into one would otherwise have taken the copy button
      away from a value people paste into letters and court forms. Same pairing
      the matter's `Deceased` row uses, for the same reason.
    */
    copyable: true,
    copyText: individualNameFormatted,
    /*
      First and last were `requiredTextField`s and stay required — the
      requirement belonged to the fields, not to their being separate rows, so
      it moves onto the composite rather than being quietly dropped. Reported
      one message at a time: naming both when both are blank reads as two
      failures of one field.
    */
    validate: (draft) => {
      if (typeof draft === 'string') return null;
      const next = draft as AttributeNameParts | null;
      if ((next?.firstName ?? '').trim().length === 0) {
        return 'First name is required.';
      }
      if ((next?.lastName ?? '').trim().length === 0) {
        return 'Last name is required.';
      }
      return null;
    },
    toPatch: (draft) => {
      if (typeof draft === 'string') return null;
      const next = draft as AttributeNameParts | null;
      return individualNamePatch({
        firstName: next?.firstName ?? '',
        middleName: blankToNull(next?.middleName ?? ''),
        lastName: next?.lastName ?? ''
      });
    }
  };
  const organisationNameField: AttributeSpec<Partial<Contact>> =
    requiredTextField(
      ORGANISATION_NAME_LABEL,
      'organisationName',
      contact.organisationName
    );

  /**
   * How this person is actually addressed — "Bob" for a Robert (VALE-989).
   *
   * Deliberately **not** an `Other names` variant. Those carry a role and a
   * reconciliation basis because they feed court-form alias recitals and the
   * affidavit "one and the same person" wording; a nickname is not an alias in
   * that sense and must never surface there. It is likewise not `alias`, which
   * is the legal also-known-as column AI extraction writes.
   *
   * Individual contacts only — an organisation is addressed by its name.
   */
  const preferredNameField: AttributeSpec<Partial<Contact>> = textField(
    PREFERRED_NAME_LABEL,
    'preferredName',
    contact.preferredName,
    {
      validate: (draft) =>
        asText(draft).trim().length > 100
          ? 'Keep a preferred name under 100 characters.'
          : null
    }
  );

  const emailField: AttributeSpec<Partial<Contact>> = {
    label: 'Email',
    display: contact.email || null,
    draft: contact.email ?? '',
    editor: { kind: 'text' },
    validate: (draft) => {
      const next = asText(draft).trim();
      if (next.length === 0) return null;
      return validateEmail(next)
        ? null
        : 'That does not look like an email address.';
    },
    toPatch: (draft) => ({ email: blankToNull(asText(draft)) })
  };

  const phoneField = textField('Phone', 'phoneNumber', contact.phoneNumber);

  const deceasedFlag: AttributeSpec<Partial<Contact>> = {
    label: 'Deceased',
    ...flagDraft(isDeceased),
    editor: { kind: 'boolean' },
    // Mirrors `ContactFormCard.onSubmit`: unticking Deceased always clears the
    // date, so a hidden `Date of death` row never carries a stale value.
    toPatch: (draft) =>
      asFlag(draft)
        ? { isDeceased: true }
        : { isDeceased: false, dateOfDeath: null },
    hidden: !isEstate
  };

  const dateOfDeathField: AttributeSpec<Partial<Contact>> = {
    ...dateField('Date of death', 'dateOfDeath', contact.dateOfDeath),
    validate: (draft) => {
      const value = asText(draft);
      if (!value) return null;
      const dod = new Date(value);
      if (Number.isNaN(dod.getTime())) return null;
      const today = new Date();
      today.setUTCHours(23, 59, 59, 999);
      if (dod.getTime() > today.getTime()) {
        return 'Date of death must be in the past.';
      }
      if (contact.dateOfBirth) {
        const dob = new Date(contact.dateOfBirth);
        if (!Number.isNaN(dob.getTime()) && dod.getTime() < dob.getTime()) {
          return 'Date of death cannot be earlier than date of birth.';
        }
      }
      return null;
    },
    hidden: !isEstate || !isDeceased
  };

  // ---------------------------------------------------------------------------
  // Role
  // ---------------------------------------------------------------------------

  // `isEstate` picks between two entirely different Role rows — RoleSection's
  // own early-return for a non-estate matter — rather than being a `hidden`
  // gate like the ones below. `hidden` exists so a row stays in the list and
  // animates smoothly when a *live, in-session* toggle changes (LPR,
  // Beneficiary, the role picker); a matter's estate/non-estate type is fixed
  // for the life of an open contact, so there is no mid-session flip for
  // `hidden` to smooth over, and giving both rows the identical "Role" label
  // in one array would make them indistinguishable to anything keying off the
  // label (including this module's own tests). The Role group's field list is
  // therefore assembled per-branch at the bottom of this function.

  /*
    **`selectWithCustom`, not `select` — the field has never actually been a
    closed set.** `contact_matters.relationship` is a nullable `varchar(50)`
    with no enum and no DTO validation, and ~6% of production rows already hold
    values outside `RELATIONSHIP_OPTIONS`: `charity` (47 rows), `mother`,
    `stepchild`, `financial adviser`, `carer`, `health attendant`. A closed
    picker did not prevent any of that; it only meant the lawyer had to reach a
    surface that could store it.

    The `display` fallback is the part that was actually broken. `selectField`
    resolves the label from the options and returns `null` when it misses, so
    every one of those rows read as *not recorded* in this drawer — the value
    was in the database and invisible to the person editing it. Falling back to
    the stored string shows what is there, whatever wrote it.
  */
  const relationshipField: AttributeSpec<Partial<Contact>> = {
    label: 'Relationship to deceased',
    display:
      RELATIONSHIP_LABELS[contact.relationshipToDeceased ?? ''] ??
      contact.relationshipToDeceased ??
      null,
    draft: contact.relationshipToDeceased ?? '',
    editor: {
      kind: 'selectWithCustom',
      options: RELATIONSHIP_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label
      })),
      customLabel: 'Something else',
      customPlaceholder: 'e.g. carer'
    },
    toPatch: (draft) => ({
      relationshipToDeceased: blankToNull(asText(draft))
    })
  };

  /*
    **`Client` is a flag, not a one-option picker** (VALE-1764).

    It rendered as a `select` whose entire vocabulary was `Client`, which is a
    control that cannot be used to say anything: the reader's only choices were
    the one value and no value. `isClient` is a boolean on the record, its two
    neighbours on the estate branch (`LPR`, `Beneficiary`) are already booleans,
    and a yes/no rendered as a list of one is the shape that made a
    non-estate matter look as though it had no role vocabulary at all.
  */
  const clientField: AttributeSpec<Partial<Contact>> = {
    label: 'Client',
    ...flagDraft(contact.isClient ?? false),
    editor: { kind: 'boolean' },
    toPatch: (draft) => ({ isClient: asFlag(draft) })
  };

  /*
    **What this person is to the clients, on a matter with no deceased.**

    The same per-matter column the estate branch edits (`contact_matters.
    relationship`, a nullable open `varchar`), bound rather than duplicated: it
    has never been an enum, and production already holds `carer`,
    `financial adviser` and `charity` in it. What was estate-only was the
    *label*, and labelling it `Relationship to deceased` on a planning matter
    is what kept the field off the surface entirely, so the only role a planning
    contact could carry was the Client flag above.

    **Appointments are deliberately NOT in this vocabulary.** Executor,
    guardian, attorney and medical treatment decision maker are per-testator
    facts on `planning_appointments`, and a couple routinely differs: David Hale
    is Margaret's executor and Geoffrey's brother-in-law. Offering `Executor`
    here would be a second definition of that fact which is wrong for one of the
    two spouses, and the Contacts tab's own sections are derived from the
    planning record for exactly that reason.
  */
  const clientRelationshipField: AttributeSpec<Partial<Contact>> = {
    label: 'Relationship',
    display:
      RELATIONSHIP_LABELS[contact.relationshipToDeceased ?? ''] ??
      contact.relationshipToDeceased ??
      null,
    draft: contact.relationshipToDeceased ?? '',
    editor: {
      kind: 'selectWithCustom',
      options: RELATIONSHIP_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label
      })),
      customLabel: 'Something else',
      customPlaceholder: 'e.g. family GP'
    },
    toPatch: (draft) => ({
      relationshipToDeceased: blankToNull(asText(draft))
    })
  };

  const lprFlag: AttributeSpec<Partial<Contact>> = {
    /*
      `LPR`, not `Legal Personal Representative` — measured, not preferred
      (LF-6306 / [ATTR-5]). The shared label column is a function of the grid's
      width, and the Contacts drawer is 672px, which resolves it to **91px**;
      the full phrase needs 182px, so it rendered as `Legal Person…` — a
      *different* phrase rather than a shortened one, on a consequential yes/no
      row. Clipping under pressure is the designed behaviour (the label yields
      before the value, {@link ATTRIBUTE_LABEL_WIDTH}), and `Relationship to
      deceased` clipping to `Relationship t…` is fine because the meaning
      survives — this one's did not.

      `LPR` is the vocabulary this product already uses for the same fact: it is
      what `contact-role-badge.tsx` renders on the very list row this drawer
      opens from, so the reader has just seen it.
    */
    label: 'LPR',
    ...flagDraft(isLpr),
    editor: { kind: 'boolean' },
    toPatch: (draft) => ({ isLegalPersonalRepresentative: asFlag(draft) })
  };

  const roleField: AttributeSpec<Partial<Contact>> = {
    label: 'Role',
    display:
      LEGAL_PERSONAL_REPRESENTATIVE_ROLES.find((r) => r.value === lprRole)
        ?.label ?? null,
    draft: lprRole ?? '',
    editor: {
      kind: 'select',
      options: LEGAL_PERSONAL_REPRESENTATIVE_ROLES.map((role) => ({
        value: role.value,
        label: role.label
      }))
    },
    // Folds RoleSection's `useEffect` reset into the commit: non-proving is
    // executor-only, so a role change away from executor (including clearing
    // the role) resets any non-proving state rather than leaving it stranded
    // behind a now-hidden control.
    toPatch: (draft) => {
      const next = blankToNull(asText(draft)) as
        | Contact['legalPersonalRepresentativeRole']
        | null;
      const patch: Partial<Contact> = { legalPersonalRepresentativeRole: next };
      if (next !== 'executor') {
        patch.isProvingLpr = true;
        patch.nonProvingReason = null;
      }
      return patch;
    },
    hidden: !isLpr
  };

  const provingField: AttributeSpec<Partial<Contact>> = {
    label: 'Proving',
    /*
      **Three states, because the column has three** (VALE-863, on VALE-795's
      `triState`). `isProvingLpr` is `boolean | null`, and `null` means the firm
      has not yet established whether this executor is proving — which is the
      state a newly-added LPR is in.

      Under `flagDraft` that rendered as a flat `No`: a determination nobody had
      made, on a row that then also drove `Non-proving reason` (hidden on
      `isProvingLpr !== false`, so it stayed hidden — the display and the
      dependent row disagreed). Worse, committing that `No` wrote
      `nonProvingReason: 'leave_reserved'`, inventing a reason for a decision
      that had not been taken.

      `triState` shows unanswered as *neither segment lit*, which is the honest
      picture, and clicking the lit segment returns to it.
    */
    ...triStateDraft(isProvingLpr),
    editor: {
      kind: 'triState',
      yes: { value: 'yes', label: 'Yes' },
      no: { value: 'no', label: 'No' },
      unsetValue: 'unknown'
    },
    /*
      `required` the same way the leads tri-states are: not "you must answer",
      but "this row has no separate empty state to clear *to*". Un-answering is
      reachable by clicking the lit segment, so a derived `×` beside it would be
      a second control for the one gesture.
    */
    required: true,
    toPatch: (draft) => {
      const answer = asText(draft);
      if (answer === 'yes')
        return { isProvingLpr: true, nonProvingReason: null };
      if (answer === 'no')
        return {
          isProvingLpr: false,
          nonProvingReason: contact.nonProvingReason ?? 'leave_reserved'
        };
      // Un-answering clears the reason with it — a reason for a decision that
      // is no longer recorded is worse than none.
      return { isProvingLpr: null, nonProvingReason: null };
    },
    hidden: !isLpr || !isExecutor
  };

  const nonProvingReasonField: AttributeSpec<Partial<Contact>> = {
    label: 'Non-proving reason',
    display: contact.nonProvingReason
      ? NON_PROVING_REASON_LABELS[contact.nonProvingReason]
      : null,
    draft: contact.nonProvingReason ?? 'leave_reserved',
    editor: {
      kind: 'select',
      options: NON_PROVING_REASONS.map((reason) => ({
        value: reason.value,
        label: reason.label
      }))
    },
    /*
      **A closed vocabulary with no absent member** — the second `required` shape,
      and the one that is easy to miss. Every non-proving executor is non-proving
      for exactly one of these reasons (LF-5372); there is deliberately no
      `Not recorded`, and the row only exists at all once `Proving` is false, at
      which point `provingField` has already defaulted it to `leave_reserved`.
      So the row is never legitimately empty, and a derived `×` would commit
      `nonProvingReason: ''` — a value the backend enum refuses and this picker
      cannot re-select.
    */
    required: true,
    // Two of the reasons are a death — mirrors
    // `LprProvingStatusField.handleReasonChange`'s side effect of flagging the
    // contact deceased so the two facts can't silently disagree.
    toPatch: (draft) => {
      const reason = asText(draft) as NonProvingReason;
      const patch: Partial<Contact> = { nonProvingReason: reason };
      if (isDeathNonProvingReason(reason)) patch.isDeceased = true;
      return patch;
    },
    hidden: !isLpr || !isExecutor || isProvingLpr !== false
  };

  const beneficiaryFlag: AttributeSpec<Partial<Contact>> = {
    label: 'Beneficiary',
    ...flagDraft(isBeneficiary),
    editor: { kind: 'boolean' },
    toPatch: (draft) => ({ isBeneficiary: asFlag(draft) })
  };

  // ---------------------------------------------------------------------------
  // Personal details — the two rows that moved back up (LF-6350 / [ATTR-8])
  // ---------------------------------------------------------------------------

  /*
    **`Date of birth` and `Minor` belong in Personal details, and lose their
    gates on the way** (LF-6350 / [ATTR-8]) — the prototype's own line: *who this
    person is* versus *what they are to this estate*. Both stay true if this
    matter were deleted tomorrow, so they sit above the split. They were under
    `Role` only because that is where their gates lived, and [ATTR-4] re-derived
    the field list from `RoleSection.tsx` — the legacy form this treatment
    replaces — which carried that layout decision straight back in.

    **The gate had to go with the move, and that is the substance rather than a
    side effect.** `isLpr` and `isBeneficiary` are switches in the section
    *below* these rows, so keeping them would make two rows blink in and out of
    Personal details as you toggle something further down the form — cause below
    effect.

    The prototype's defence, adopted here explicitly: the economics inverted with
    the treatment. An unset *input box* costs 54px and reads as a question you
    failed to answer, so hiding it earns its complexity; an unset *attribute row*
    costs 30px and reads `Set Date of birth…`, which is simply the truth. Hiding
    a fact because it is currently unrecorded is worth 54px and is not worth
    30px. (LF-5825 reverted an ungating exactly like this one, which is why the
    ticket flags it as the call most worth challenging at review.)

    **The organisation split is no longer a `hidden` gate on these two rows** —
    as of [ATTR-9] / LF-6359 §4 they sit inside the type branch below, with the
    name rows. Story 7a's own distinction: an organisation having no birthday is
    a statement about which fields *exist* for this subject, not about which of
    its fields are currently relevant, and `hidden` is for the latter (a row that
    collapses while the ones below it glide into the gap). Both are still
    individual-only; the branch is simply where that is now said, once.
  */
  const dateOfBirthField: AttributeSpec<Partial<Contact>> = dateField(
    'Date of birth',
    'dateOfBirth',
    contact.dateOfBirth
  );

  const minorFlag: AttributeSpec<Partial<Contact>> = {
    label: 'Minor',
    ...flagDraft(isMinor),
    editor: { kind: 'boolean' },
    // `isMinor` is UI-only (never sent); the persisted field is `isOfAge`, its
    // inverse — see the module doc.
    toPatch: (draft) => ({ isOfAge: !asFlag(draft) }),
    /*
      **The one contact row the diff calls something other than what it writes**
      (VALE-1375).

      `contractKeysFor` asks `toPatch` what this row writes and gets `isOfAge`,
      but an AI extract's `fieldChanges` is keyed by what the reviewer READS —
      `isMinor`. So the treatment never matched this row to its own change: the
      live `Minor` control rendered unmarked, and the unmatched `isMinor` change
      was appended a second time as a dead read-only row labelled `Is Minor`.
      One fact, drawn twice, neither of them right.

      `readKey` rather than `diffKey`, and the distinction is load-bearing —
      `diffKey` REPLACES the key list and skips `toPatch`, which dropped this
      row from the review block the moment a reviewer edited it (`editedKeys`
      records what a commit WROTE, `isOfAge`). See `contractKeysFor`.
    */
    readKey: 'isMinor'
  };

  // ---------------------------------------------------------------------------
  // Bank account
  // ---------------------------------------------------------------------------

  const bankAccount = contact.bankAccount;

  /**
   * **The draft the popover opens with** — the stored parts, with the verified
   * flag read from `isBankVerified` rather than from `bankAccount.isVerified`.
   *
   * That asymmetry is LF-3696's and it is deliberate: `isBankVerified` is the
   * **matter-scoped** flag, which is what this surface must display, while the
   * write goes to `bankAccount.isVerified`, which is the key the backend
   * actually re-derives the matter-scoped one from (`contacts.service.ts`:
   * `contactMatter.isBankVerified = bankAccount?.isVerified === true`).
   * `isBankVerified` is response-only — absent from `UpdateContactRequestSchema`
   * and from both matter-contact DTOs — so a patch carrying it is stripped by
   * Zod and arrives empty, which is the save-that-changed-nothing LF-6359 spent
   * a ticket finding. Read one key, write the other.
   *
   * `null` when nothing is captured at all, so an empty account renders the
   * row's `Set bank account…` placeholder rather than an object of empty
   * strings.
   */
  const bankAccountDraft: AttributeBankAccount | null =
    bankAccount || isBankVerified
      ? {
          bsb: bankAccount?.bsb ?? '',
          accountNumber: bankAccount?.accountNumber ?? '',
          accountName: bankAccount?.accountName ?? '',
          isVerified: isBankVerified,
          verificationNote: bankAccount?.verificationNote ?? ''
        }
      : null;

  /**
   * **The whole account as one row** (VALE-1211).
   *
   * This was five rows — `BSB`, `Account number`, `Account name`, `Verified`
   * and `Verification note` — each with its own editor and its own commit, on a
   * surface where the same account is printed as a single line one panel over. A
   * BSB alone answers nothing; the parts are read together everywhere they
   * appear, so they are now edited together and land as one patch.
   *
   * ## What the merge fixes, beyond the row count
   *
   * VALE-91 caught the split rows producing a refusal cascade on this exact
   * field set: typing a BSB alone gave *"five sentences, `not saved` twice, two
   * contradictory rules fired on an empty field the reader never touched"*. One
   * editor validating one object can only refuse the thing the reader is
   * actually editing, which is why {@link validate} below reads the whole
   * account and names the part at fault.
   *
   * ## The BSB's rule is widened, deliberately
   *
   * The old rule was `/^\d{6}$/` and the comment above it explained at length
   * why it did **not** bind to the shared BSB helpers: they strip separators,
   * and accepting hyphenated input *"the current Contacts form rejects"* would
   * be a behaviour change rather than a restatement fix.
   *
   * That reasoning was right for a bare text box and does not survive the
   * popover, because `AttributeBankAccountEditor` now **inserts the separator
   * itself** — keeping the strict rule would be the field refusing its own
   * output. So the digits are extracted first ({@link bsbDigits}) and the
   * six-digit rule applies to those. The stored value is unchanged: `toPatch`
   * commits digits, exactly as before.
   *
   * The account-number rule is untouched — 6–9 digits, no formatting tolerance —
   * because nothing formats it and it still differs from the two shared helpers,
   * which disagree with each other on the upper bound (9 vs 10).
   */
  const bankAccountField: AttributeSpec<Partial<Contact>> = {
    /*
      **`Account`, not `Bank account`** — the `BANK ACCOUNT` group heading
      directly above already says it, and repeating it would spend the shared
      label column (which resolves to ~91px on this drawer) twice on the same
      word. The same reasoning made the old flag `Verified` rather than
      `Bank verified` (LF-6350), and it binds harder here: the row and its
      heading would otherwise be the identical string, which reads as a
      mis-render rather than as a heading.
    */
    label: 'Account',
    /*
      **The row owns its whole line** (VALE-1211). Measured, not assumed: at the
      inherited two columns the value truncated to `062-00…` with the entire
      right half of the block empty, because the read-out carries the two
      numbers *and* the `Verified` badge inside one target and the column is
      roughly half the surface.

      `fullWidth` also makes the value wrap rather than ellipsise (derived in
      `attribute-read-out.tsx`), which is the behaviour wanted here for the same
      reason it is wanted on an address: a truncated identifier is worse than a
      wrapped one, and an account number a reader cannot finish reading is the
      whole failure this row exists to fix.
    */
    fullWidth: true,
    display: formatBankAccountSummary(bankAccountDraft) || null,
    draft: bankAccountDraft,
    /*
      **The verified chip, and only when verified** (VALE-1211). Read from
      `isBankVerified` for LF-3696's reason — the matter-scoped flag is what the
      badge must display — while the write below goes to `bankAccount.isVerified`.
    */
    badge: isBankVerified ? bankAccountVerifiedBadge() : null,
    // The two numbers, without the read-out's `·` — a payment form rejects it.
    copyText: bankAccountCopyText(bankAccountDraft),
    editor: {
      kind: 'bankAccount',
      accountName: true,
      verified: true,
      /*
        A note about a verification that has not happened says nothing — the same
        gate `BankAccountSection`'s `showFileNote` prop derives, and the same one
        the standalone row carried. The *verified* half of the old `hidden`
        expression is not repeated here: the editor reveals the note from the live
        draft, so ticking the flag surfaces it in the same gesture instead of on
        the next render.
      */
      verificationNote: hasMatterContext && isEstate && isBeneficiary
    },
    validate: (draft) => {
      if (draft === null || typeof draft !== 'object') return null;
      const account = draft as AttributeBankAccount;

      const bsb = bsbDigits(account.bsb);
      // An empty part is not an error: a BSB captured days before the account
      // number is the ordinary state of an account during discovery.
      if ((account.bsb ?? '').trim() && bsb.length !== 6) {
        return 'BSB must be exactly 6 digits.';
      }

      const number = (account.accountNumber ?? '').trim();
      if (number) {
        if (!/^\d+$/.test(number)) {
          return 'Account number must contain only digits.';
        }
        if (number.length < 6 || number.length > 9) {
          return 'Account number must be 6-9 digits.';
        }
      }

      return null;
    },
    toPatch: (draft) => {
      const account =
        draft && typeof draft === 'object'
          ? (draft as AttributeBankAccount)
          : null;

      /*
        **Spread-preserve, exactly as all five rows did.** `bankAccount` is sent
        whole, and it carries four keys this editor does not show — `country`,
        `swiftBic`, `internationalAccountNumber`, `bankName`, the overseas
        details. Rebuilding the object from the editor's own parts would wipe a
        non-Australian account's details on the first commit, and nothing in the
        type system or the suite would have said so.

        That is also why clearing the row (the `×`, which commits `null`) nulls
        only the five parts this editor owns rather than sending `bankAccount:
        null`: a reader clearing a row they can see must not silently delete
        details it never showed them.
      */
      const bsb = bsbDigits(account?.bsb);
      return {
        bankAccount: {
          ...(bankAccount ?? {}),
          // Digits only — the backend stores six, and the separator is display.
          bsb: bsb || null,
          accountNumber: blankToNull(account?.accountNumber ?? ''),
          accountName: blankToNull(account?.accountName ?? ''),
          isVerified: account?.isVerified === true,
          verificationNote: blankToNull(account?.verificationNote ?? '')
        }
      };
    }
  };

  // ---------------------------------------------------------------------------
  // Notes
  // ---------------------------------------------------------------------------

  const matterNotesField: AttributeSpec<Partial<Contact>> = {
    /*
      **`Matter notes`, under a `NOTES` heading** — back where LF-6350 left it,
      after VALE-863 tried both alternatives on the running build and each was
      worse.

      Dropping the heading (so the rows carried the only "notes") put them
      directly under `BANK ACCOUNT` with nothing but section spacing between,
      and they read as part of that section — spacing alone is not a boundary a
      reader sees. Trimming the labels to `Matter` / `General` under the heading
      kept the boundary but the rows stopped making sense on their own: a row
      labelled `Matter` inside a contact record reads as *which matter*, not
      *the notes for it*.

      So the heading stays for the boundary and the labels stay whole for the
      sense, and the mild repetition of the word is accepted as the cheapest of
      the three costs. Tom's 27 Aug note asked for the duplication to go; this
      is a deliberate decision not to pay for that with either of the above, and
      it should be re-raised with him rather than quietly re-fixed.
    */
    label: 'Matter notes',
    display: contact.matterNotes || null,
    draft: contact.matterNotes ?? '',
    editor: { kind: 'text' },
    multiline: true,
    fullWidth: true,
    toPatch: (draft) => ({ matterNotes: blankToNull(asText(draft)) }),
    hidden: !hasMatterContext
  };

  const generalNotesField: AttributeSpec<Partial<Contact>> = {
    /** `General notes`, for the reason {@link matterNotesField} carries. */
    label: 'General notes',
    display: contact.notes || null,
    draft: contact.notes ?? '',
    editor: { kind: 'text' },
    multiline: true,
    fullWidth: true,
    toPatch: (draft) => ({ notes: blankToNull(asText(draft)) }),
    hidden: !showGeneralNotes
  };

  /*
    **The order and the grouping are story 7a's** (LF-6350 / [ATTR-8]), matched
    against `regroupedSections` in
    `components/prototypes/form-system/contact-attribute-specs.tsx` — the
    epic's chosen direction, and the reference [ATTR-4] should have been read
    against instead of the form it was replacing.

    Four rows in this list have **no counterpart in the prototype's mock**, because
    its flat `ContactAttributeValues` had no field for them: `Non-proving reason`,
    `Account name`, `General notes`, and the non-estate `Role — Client` branch.
    Each is slotted into the nearest position in 7a's own ordering rather than the
    ordering being reshaped around it — non-proving directly under `Proving` (the
    control that reveals it), account name after the number it belongs to, general
    notes under the matter notes it qualifies.
  */
  const groups: ContactAttributeGroup[] = [
    {
      title: PERSONAL_DETAILS_GROUP_TITLE,
      fields: [
        /*
          Story 7a's branch, verbatim: an organisation is its name, and the four
          rows below the names go with the individual they describe. *"An
          organisation has no birthday"* — that is a property of the subject
          rather than a layout gate, which is why it is a branch here and a
          `hidden` flag on `Date of death` (whose gate, `Deceased`, is a value on
          this same record).

          Email, phone and address are outside the branch because both kinds of
          contact have them.
        */
        /*
          **`Contact type` opens the section, alone on its line** (VALE-863).

          It gates everything below it — the name row is one field or two, and
          the four person-only rows exist at all, depending on this answer — so
          a reader meets the question that shapes the form before the form it
          shapes. `fullWidth` is what keeps it from pulling `Name` up beside it:
          the classification is not a peer of the identity block, and pairing
          them would read as though it were.
        */
        { ...contactTypeField, fullWidth: true },
        /*
          **The identity block follows as a 2×2** (VALE-863): the name
          and the address on the first line, and the collections that extend
          each of them — `Other names`, `Other addresses` — directly underneath
          their own scalar on the second. The grid inserts those two rows; see
          `spliceVariantFields` in `contact-attribute-grid.tsx`.

          The address moved up out of the contact block for it. It reads as
          identity rather than as a way to reach someone — a letter goes to it,
          but so does the question *"is this the right person?"* — and pairing
          it with the name is what makes the four rows one shape instead of two
          unrelated pairs.
        */
        ...(isOrg
          ? [organisationNameField, addressAttribute(contact)]
          : [individualNameField, addressAttribute(contact)]),
        ...(isOrg
          ? []
          : [
              /*
                `Preferred name` follows the 2×2 identity block, which puts it at
                the head of the next line — directly below `Other names`, itself
                below `Name`. That is the placement the block's own rule asks
                for: *"a reader scanning the left column gets every name"*. It is
                not spliced into the 2×2 itself, which pairs each scalar with the
                collection that extends it; a preferred name extends neither.
              */
              preferredNameField,
              // Who the person is, ahead of how to reach them — and both
              // ungated within this branch, see their declarations above.
              dateOfBirthField,
              minorFlag,
              deceasedFlag,
              dateOfDeathField
            ]),
        emailField,
        phoneField
      ]
    },
    {
      /*
        `Role in the estate`, not `Role` — 7a's title, and the qualifier is what
        makes the split legible: it is the half of *who this person is* versus
        *what they are to this estate* that names the estate. A bare `Role` reads
        as a job title.

        On a non-estate matter there is no estate to name, so the title switches
        to `Role on this matter` — the same "on this matter" idiom the product
        already uses elsewhere for matter-generic (non-estate) copy, rather than
        inventing a new phrase (VALE-326). Both titles are exported constants:
        the grid's host-level gate keys off these exact strings (a global
        contact has no roles surface at all) — see `MATTER_SCOPED_GROUPS` in
        `contact-attribute-grid.tsx`, which moved with it.
      */
      title: isEstate ? ROLE_GROUP_TITLE_ESTATE : ROLE_GROUP_TITLE_NON_ESTATE,
      fields: isEstate
        ? /*
            **Tom's 27 Aug layout**: the two role *answers* run down the left
            column, `Relationship to deceased` sits opposite the first of them
            (VALE-863). Reading order was `Relationship, LPR, Role, …`, which
            put the estate-role question after the family one and split the two
            Yes/No rows across different lines.

            Each left-column row declares `startsRow`, because three of the six
            rows here are conditional (`Role`, `Proving`, `Non-proving reason`)
            and a `hidden` row shifts the column parity of everything after it —
            ticking `LPR` would otherwise re-pair the whole section.
          */
          [
            { ...lprFlag, startsRow: true },
            relationshipField,
            { ...beneficiaryFlag, startsRow: true },
            roleField,
            { ...provingField, startsRow: true },
            nonProvingReasonField
          ]
        : /* The non-estate pair: whether this person is one of the clients,
             and what they are to them. `startsRow` on the flag for the same
             reason the estate branch sets it, so the two read as one line. */
          [{ ...clientField, startsRow: true }, clientRelationshipField]
    },
    {
      title: 'Bank account',
      /*
        **One row** (VALE-1211). VALE-863 ordered the five rows this replaces —
        `Account name` / `Verified` first, then `BSB` / `Account no.`, with
        `startsRow` anchors holding the pairs against the conditional note's
        parity shift — on the reasoning that whose account it is and whether
        anyone checked it are read first, and the numbers are what a reader
        copies once they trust it.

        That ordering problem is gone rather than re-solved: there is one row, it
        reads out the two numbers with the verified state as its badge, and the
        parity shift it was defending against cannot happen. The reading priority
        VALE-863 established is preserved — the account is still *identified*
        before it is *transcribed* — it is just no longer expressed as row order.
        See {@link bankAccountField} for the order **inside** the popover, which
        is capture order and deliberately different.
      */
      fields: [bankAccountField]
    },
    {
      /*
        **The heading is what separates these rows from `BANK ACCOUNT`**
        (VALE-863). Running headless put them directly under the bank rows with
        only section spacing between, and they read as belonging to that
        section — see {@link matterNotesField} for why the labels then went back
        to their full form too.
      */
      title: 'NOTES',
      fields: [matterNotesField, generalNotesField]
    }
  ];

  if (!enquiryContact) return groups;

  return [
    groups[0],
    {
      title: ROLE_GROUP_TITLE_ESTATE,
      fields: [
        { ...lprFlag, startsRow: true },
        relationshipField,
        {
          ...roleField,
          startsRow: true,
          clearTo: '',
          display:
            LEGAL_PERSONAL_REPRESENTATIVE_ROLES.find(
              (role) => role.value === lprRole
            )?.label ?? 'Not yet known'
        }
      ]
    }
  ];
}

// =============================================================================
// The PMS participant-type row (VALE-158)
// =============================================================================

/**
 * The row's label — **not** prefixed with the provider's name.
 *
 * The provider is already named once, by the section heading this row sits
 * under (`ActionStep`, with that provider's own mark). Repeating it here would
 * spend the shared label column twice on the same word: the column resolves to
 * **91px** on the 672px Contacts drawer, and `ActionStep Participant Type`
 * needs roughly 175px, so it would not shorten — it would clip to a *different*
 * phrase, which is the exact failure that forced `LPR` over
 * `Legal Personal Representative` (see {@link lprFlag}).
 *
 * `Participant type` is ActionStep's own vocabulary for this concept — the
 * catalogue it comes from is the action type's participant types — so the
 * heading and the row together read as the provider's own language rather than
 * as a Vale coinage.
 *
 * **Sentence case, not ActionStep's own title case, and that is forced rather
 * than preferred.** The empty row stands in with `attributePlaceholder`, which
 * decapitalises only the label's *first* word (it is acronym-safe, so `BSB`
 * survives). A title-case `Participant Type` therefore renders
 * `Set participant Type…` — a mid-sentence capital, which is precisely the
 * defect VALE-91 fixed and VALE-130 centralised the derivation to prevent. That
 * module explicitly refuses a per-field `placeholder` override as the
 * call-site-remembers-a-prop antipattern (`components/attributes/CLAUDE.md`
 * §2), so the label is what has to give. It also puts this row in line with
 * every sibling on the surface — `Contact type`, `Date of birth`,
 * `Account name`, `Non-proving reason` are all sentence case.
 */
export const PMS_PARTICIPANT_TYPE_LABEL = 'Participant type';

/** What the row needs from the host that only a hook can answer. */
export interface PmsParticipantTypeFieldOptions {
  /**
   * The firm's catalogue, **already sorted** — the sort belongs to
   * `getPmsContactTypes` so both this surface and the contact card offer one
   * order. Never re-sort here.
   */
  options: { value: string; label: string }[];
  /** The contact's stored selection on this matter, or `null` if unfiled. */
  selectedTypeId: string | null;
  /**
   * The stored selection's label, read back from the binding row rather than
   * looked up in {@link options} — which is what lets an existing selection
   * render when the PMS is unreachable and the catalogue never loaded.
   */
  selectedTypeLabel: string | null;
}

/**
 * **Which PMS participant type this contact is filed under on this matter**, as
 * an attribute row (VALE-158).
 *
 * The card's equivalent is `PmsContactTypeSelector`, which is a
 * `react-hook-form` control submitted with the whole card. This is the same
 * decision on the commit-on-blur path, and the mapping turns out to be exact:
 * the row's patch reaches `updateContact` — the identical endpoint the card
 * submits to — where `ContactsService` hands it to
 * `PmsContactTypeSelectionService.saveSelection`. **A per-field commit is if
 * anything the better fit**: [CTBIND-6] needs stored-row change detection
 * because the card re-submits every field on every save, whereas this row only
 * ever sends the field that actually changed.
 *
 * **Both keys travel in one patch.** `toPatch` returns a single object, so the
 * id and its label commit together — and storing the label is what lets the
 * selection render back with no catalogue call at all.
 *
 * **It offers no `×`, and that is a data rule rather than chrome.** A `select`
 * derives a clear affordance by default (`offersClear`), but clearing here
 * would commit an empty `pmsContactTypeId` — which is not an unbind: it would
 * neither remove the PMS link nor pass `saveSelection`, which takes a
 * `string`. Removing a contact from the matter in the PMS is a deliberate,
 * confirmed action with its own endpoint, and it lives on
 * `PmsContactBindingRow`'s **Unbind** in the block directly beneath this row.
 * `required` is how that is said (see {@link AttributeSpec.required}'s second
 * shape: a closed vocabulary with no absent member).
 */
export function buildPmsParticipantTypeField({
  options,
  selectedTypeId,
  selectedTypeLabel
}: PmsParticipantTypeFieldOptions): AttributeSpec<Partial<Contact>> {
  return {
    label: PMS_PARTICIPANT_TYPE_LABEL,
    /*
      The stored label wins over a catalogue lookup, so a filed contact still
      reads correctly when the PMS is unreachable and `options` is empty. The
      lookup is the fallback for the one case the stored label cannot cover —
      a selection made moments ago in this same session, before the bindings
      query has refetched.
    */
    display:
      selectedTypeLabel ??
      options.find((option) => option.value === selectedTypeId)?.label ??
      null,
    draft: selectedTypeId ?? '',
    editor: { kind: 'select', options },
    required: true,
    validate: (draft) =>
      asText(draft).trim().length === 0
        ? `${PMS_PARTICIPANT_TYPE_LABEL} is required. Use Unbind to remove this contact from the matter in your PMS.`
        : null,
    toPatch: (draft) => {
      const nextId = asText(draft);
      if (nextId.length === 0) return null;
      return {
        pmsContactTypeId: nextId,
        pmsContactTypeLabel:
          options.find((option) => option.value === nextId)?.label ?? null
      };
    }
  };
}
